import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Slider } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import Box from '@mui/material/Box';
import { format, addHours, differenceInDays, parseISO } from 'date-fns';
import './slider_forecast_metric.css';

const TimeSliderForecastMetric = ({
  initialDate,
  steps,
  onChange,
  cqlFechaMin,
  issueTime
}) => {

  // 1. CALCULAR COMPORTAMIENTO (Memoizado para reactividad)
  const sliderBehavior = useMemo(() => {
    // Valores por defecto si faltan datos
    if (!cqlFechaMin || !issueTime) return {
      initialValue: 0, minValue: 0, maxValue: steps - 1, disabled: false, blockedPositions: [], dayDifference: null
    };

    try {
      const cqlDate = parseISO(cqlFechaMin);
      const issueDate = parseISO(issueTime);

      // Diferencia de días (sin horas)
      const cqlDateMidnight = new Date(cqlDate.getFullYear(), cqlDate.getMonth(), cqlDate.getDate());
      const issueDateMidnight = new Date(issueDate.getFullYear(), issueDate.getMonth(), issueDate.getDate());
      const dayDifference = differenceInDays(cqlDateMidnight, issueDateMidnight);

      // Hora de emisión (ej: si issueTime es ...07:00:00, issueHour es 7)
      const issueHour = issueDate.getHours();

      let initialValue = 0;
      let blockedPositions = [];

      switch (dayDifference) {
        case 0: // D-0: Hoy para Hoy
          // 🔥 CORRECCIÓN CLAVE: El slider inicia en la hora de emisión (ej: 7)
          initialValue = issueHour;
          // Bloqueamos las horas anteriores a la emisión
          blockedPositions = Array.from({ length: issueHour }, (_, i) => i);
          break;

        case 1: // D-1: Ayer para Hoy (Todo disponible)
          initialValue = 0;
          blockedPositions = [];
          break;

        case 2: // D-2: Hoy para Pasado Mañana (Bloqueo futuro)
          initialValue = 0;
          // Bloqueo estándar de +4 horas (ajustar según tu lógica de negocio)
          const blockFromHour = issueHour + 4;

          if (blockFromHour >= steps) {
            blockedPositions = Array.from({ length: steps }, (_, i) => i);
          } else {
            blockedPositions = Array.from({ length: steps - blockFromHour }, (_, i) => i + blockFromHour);
          }
          break;

        default:
          initialValue = 0;
          blockedPositions = [];
      }

      return {
        initialValue, minValue: 0, maxValue: steps - 1, disabled: false, dayDifference, issueHour, blockedPositions
      };
    } catch (error) {
      console.error('Error calculating slider behavior:', error);
      return { initialValue: 0, minValue: 0, maxValue: steps - 1, disabled: false, blockedPositions: [] };
    }
  }, [cqlFechaMin, issueTime, steps]);

  // 2. ESTADOS
  const [sliderValue, setSliderValue] = useState(sliderBehavior.initialValue);
  const [startDate, setStartDate] = useState(new Date(cqlFechaMin));
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [adjustedDateTime, setAdjustedDateTime] = useState('');

  const previousIssueTime = useRef(issueTime);
  const previousCqlFechaMin = useRef(cqlFechaMin);

  // Generación de marcas (Cada 4 horas por ejemplo, o todas si son pocas)
  const marks = useMemo(() => Array.from({ length: steps }, (_, i) => {
    // Si steps es 24 (1 hora), mostramos marca cada 2 o 4 horas para no saturar
    // O mostramos todas si steps es pequeño. Aquí asumimos steps=24 aprox.
    const showLabel = (i % 2 === 0);
    const markDate = addHours(new Date(cqlFechaMin), i);
    const isBlocked = sliderBehavior.blockedPositions.includes(i);
    return {
      value: i,
      label: showLabel ? format(markDate, 'H') : '',
      blocked: isBlocked
    };
  }), [cqlFechaMin, steps, sliderBehavior.blockedPositions]);
  console.log(marks);
  const handlePlayButtonClick = () => { setIsAutoPlay(!isAutoPlay); setIsPlaying(!isPlaying); };

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  const debouncedOnChange = useCallback(debounce((selectedDate) => { onChange(selectedDate.toISOString()); }, 500), [onChange]);

  const handleSliderChange = (event, value) => {
    if (sliderBehavior.blockedPositions.includes(value)) return;

    setSliderValue(value);
    const selectedDate = addHours(new Date(cqlFechaMin), value);
    debouncedOnChange(selectedDate);
    setAdjustedDateTime(format(selectedDate, 'yyyy-MM-dd HH:mm:ss'));
  };

  // 3. EFECTO DE SINCRONIZACIÓN
  useEffect(() => {
    const issueTimeChanged = previousIssueTime.current !== issueTime;
    const cqlFechaMinChanged = previousCqlFechaMin.current !== cqlFechaMin;

    if ((issueTimeChanged || cqlFechaMinChanged) && cqlFechaMin) {
      // 🔥 AL ACTUALIZARSE: Saltamos a la posición inicial calculada (la hora Issue)
      setSliderValue(sliderBehavior.initialValue);
      const initialD = addHours(new Date(cqlFechaMin), sliderBehavior.initialValue);
      setAdjustedDateTime(format(initialD, 'yyyy-MM-dd HH:mm:ss'));
      onChange(initialD.toISOString());

      previousIssueTime.current = issueTime;
      previousCqlFechaMin.current = cqlFechaMin;
    }
  }, [issueTime, cqlFechaMin, sliderBehavior.initialValue, onChange]);

  // Autoplay
  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          let nextValue = prevValue + 1;
          while (nextValue <= sliderBehavior.maxValue && sliderBehavior.blockedPositions.includes(nextValue)) {
            nextValue++;
          }
          if (nextValue > sliderBehavior.maxValue) {
            nextValue = sliderBehavior.initialValue;
            while (sliderBehavior.blockedPositions.includes(nextValue) && nextValue <= sliderBehavior.maxValue) {
              nextValue++;
            }
          }
          const newDateTime = addHours(startDate, nextValue);
          debouncedOnChange(newDateTime);
          setAdjustedDateTime(format(newDateTime, 'yyyy-MM-dd HH:mm:ss'));
          return nextValue;
        });
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, startDate, debouncedOnChange, sliderBehavior]);

  useEffect(() => { setStartDate(new Date(cqlFechaMin)); }, [cqlFechaMin]);

  // CSS Classes
  const getSliderClasses = () => {
    let classes = "forecast-metric-slider__slider";
    if (sliderBehavior.dayDifference === 0) classes += " forecast-metric-slider--d0";
    else if (sliderBehavior.dayDifference === 2) classes += " forecast-metric-slider--d2";
    return classes;
  };

  // 4. PREPARAR FECHA BASE PARA TOOLTIP
  // Protección defensiva para inicio
  const baseString = issueTime || cqlFechaMin || new Date().toISOString();
  const issueBaseDate = new Date(baseString);
  const isValidDate = !isNaN(issueBaseDate.getTime());
  if (isValidDate) issueBaseDate.setHours(0, 0, 0, 0);

  return (
    <div className="forecast-metric-slider">
      <div className="forecast-metric-slider__container">
        <button
          className={`forecast-metric-slider__button ${isAutoPlay ? 'forecast-metric-slider__button--stop' : 'forecast-metric-slider__button--play'}`}
          onClick={handlePlayButtonClick}
          type="button"
        >
          {isAutoPlay ? <StopCircleRoundedIcon className="forecast-metric-slider__icon" /> : <PlayCircleOutlineRoundedIcon className="forecast-metric-slider__icon" />}
        </button>

        <Box
          className="forecast-metric-slider__wrapper"
          sx={{
            // Calcula el porcentaje de bloqueo inicial basado en la hora Issue
            '--blocked-percentage': sliderBehavior.dayDifference === 0
              ? `${(sliderBehavior.issueHour / (steps - 1)) * 100}%`
              : '0%'
          }}
        >
          <Slider
            value={sliderValue}
            step={1}
            marks={marks}
            min={0}
            max={steps - 1}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            // Formato con Año-Mes-Dia Hora:Min UTC
            valueLabelFormat={(value) => {
              if (!isValidDate) return "";
              // Usamos addHours para el slider normal
              return format(addHours(issueBaseDate, value), "yyyy-MM-dd HH:mm 'UTC'");
            }}
            className={getSliderClasses()}
            
            classes={{
              root: 'forecast-metric-slider__root',
              rail: 'forecast-metric-slider__rail',
              track: 'forecast-metric-slider__track',
              thumb: 'forecast-metric-slider__thumb',
              mark: 'forecast-metric-slider__mark',
              markActive: 'forecast-metric-slider__mark--active',
              markLabel: 'forecast-metric-slider__mark-label',
              valueLabel: 'forecast-metric-slider__value-label',
            }}
          />
        </Box>
      </div>
    </div>
  );
};

export default TimeSliderForecastMetric;